import { ChangeEventHandler, useState } from 'react'

import { differenceBy, take } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { connect } from 'scrivito'

import { RelationalSublistConfigs } from './config'
import { StringText } from '@/Atomic/Atoms/StringText'
import { Search } from '@/Atomic/Molecules/Inputs/Search'
import {
  getDataItemFromConnection,
  linkDataItem,
  mapSublistItem,
  SublistBody,
  SublistSearchResult,
} from '@/Atomic/Organisms/RelationalSublist'
import { Sublist, SublistFooter } from '@/Atomic/Templates/Sublist'
import { DataClassName } from '@/Data/const'
import { useDataClassIndex } from '@/hooks/useDataClassIndex'
import { useFlashSublistItem } from '@/hooks/useFlashSublistItem'
import { RelationSublistConfigKey } from '@/utils/CRUD/read/relational/const'
import { getEntityLabelKey } from '@/utils/getEntityLabelKey'

import './RelationDataItemSublist.scss'

interface RelationalDataItemSublistProps {
  id: string
  configKey: RelationSublistConfigKey
}

export const RelationalDataItemSublist: React.FC<RelationalDataItemSublistProps> =
  connect(({ id, configKey }) => {
    const { t } = useTranslation()
    const [search, setSearch] = useState('')
    const { flashId: flashItemId, flash: flashItemIdNow } =
      useFlashSublistItem()

    const onSearchChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      setSearch(event.target.value)
    }

    const config = RelationalSublistConfigs[configKey]

    const {
      relationalDataClass: {
        dataClass,
        filter: { keys, values },
      },
      getter,
      setter,
    } = config

    let transform

    if (keys.dist) {
      transform = {
        filters: {
          [keys.src]: id,
          [keys.dist]: values?.dist,
          [keys.search]: search,
        },
      }
    } else {
      transform = {
        filters: {
          [keys.src]: id,
          [keys.search]: search,
        },
      }
    }

    // existing connections
    const {
      dataItems: dataItemConnections,
      loadMore,
      hasMore,
      count,
    } = useDataClassIndex(dataClass, transform, 5)

    const items = dataItemConnections.flatMap((el) => {
      const { dataItem } = getDataItemFromConnection(el, getter) || {}
      if (!dataItem) return []
      return mapSublistItem(dataItem, el)
    })

    // search results (not connected yet)
    const { dataItems: searchResultDataItems } = useDataClassIndex(
      getter.dataClass,
      { search },
      5,
    )

    const searchResultItems = searchResultDataItems.map((el) =>
      mapSublistItem(el),
    )

    // remove existing links from search results
    const filteredSearchResults = differenceBy(searchResultItems, items, 'id')

    const searchResults =
      search && items.length < 5
        ? take(filteredSearchResults, 5 - items.length)
        : []

    const doLink = linkDataItem(dataClass, setter, id)
    const linkSearchResult = async (relatedId: string) => {
      await doLink(relatedId)
      flashItemIdNow(relatedId)
    }

    const countDataClassName =
      configKey === RelationSublistConfigKey.ACTIVITY_COMPANY ||
      configKey === RelationSublistConfigKey.PERSON_COMPANY
        ? DataClassName.COMPANY
        : DataClassName.PERSON
    const searchPlaceholder =
      countDataClassName === DataClassName.COMPANY
        ? t('search.companyPlaceholder')
        : t('search.personPlaceholder')

    return (
      <Sublist>
        <StringText
          variant="title"
          className="title-font"
          value={t(config.titleKey)}
        />
        <Search
          name={`search sublist ${configKey}`}
          onChange={onSearchChange}
          searchWrapperClassName="sublist-search-bar"
          placeholder={searchPlaceholder}
        />
        <SublistBody items={items} flashItemId={flashItemId} />
        <SublistSearchResult
          items={searchResults}
          link={linkSearchResult}
          flashItemId={flashItemId}
        />
        {!search && (
          <SublistFooter
            amount={items.length}
            total={count}
            labelKey={getEntityLabelKey(countDataClassName)}
            onLoadMore={hasMore ? loadMore : undefined}
          />
        )}
      </Sublist>
    )
  })
